import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "../css/SectionManager.css";

const API_URL = "";
const api = axios.create({ baseURL: API_URL });

/* ───────────────── Toast ──────────────── */

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`toast ${type}`}>
      {message}
    </div>
  );
}

/* ───────────────── Toggle ───────────────── */

function Toggle({ checked, onChange }) {
  return (
    <button className={`toggle ${checked ? "on" : ""}`} onClick={onChange}>
      <span />
    </button>
  );
}

 /* ───────────────── Status Buttons ───────────────── */

function StatusButtons({ checked, onChange }) {
  return (
    <div className="statusButtons">
      
      <button
        className={`statusBtn ${!checked ? "active" : ""}`}
        onClick={() => !checked || onChange()}
      >
        Active
      </button>

      <button
        className={`statusBtn ${checked ? "published" : ""}`}
        onClick={() => checked || onChange()}
      >
        Published
      </button>

    </div>
  );
}

/* ───────────────── Section Row ───────────────── */

function SectionRow({ section, pageId, onToggle, saving }) {
  const key = `${pageId}_${section.sectionId}`;
  const loading = saving === key;

  return (
    <div className="sectionRow">

      <div className="sectionInfo">
        <span className={`dot ${section.isVisible ? "active" : ""}`} />

        <div>
          <div className="sectionTitle">{section.label}</div>
          <div className="sectionId">{section.sectionId}</div>
        </div>
      </div>

      {loading ? (
        <div className="spinner"></div>
      ) : (
        <StatusButtons
  checked={section.isVisible}
  onChange={() =>
    onToggle(pageId, section.sectionId, section.isVisible)
  }
/>
        // <Toggle
        //   checked={section.isVisible}
        //   onChange={() =>
        //     onToggle(pageId, section.sectionId, section.isVisible)
        //   }
        // />
      )}

    </div>
  );
}

/* ───────────────── Page Card ───────────────── */

function PageCard({ page, onToggle, saving }) {

  const visibleCount = page.Sections.filter((s) => s.isVisible).length;

  return (
    <div className="pageCard">

      <div className="pageHeader">
        <div>
          <h3>{page.label}</h3>
          <span>{visibleCount}/{page.Sections.length} visible</span>
        </div>
      </div>

      <div className="sectionList">
        {(page.Sections || []).map((section) => (
          <SectionRow
            key={section.sectionId}
            section={section}
            pageId={page.pageId}
            onToggle={onToggle}
            saving={saving}
          />
        ))}
      </div>

    </div>
  );
}

/* ───────────────── Main Component ───────────────── */

export default function SectionManager() {

  const [pages, setPages] = useState([]);
  const [status, setStatus] = useState("loading");
  const [saving, setSaving] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  /* Sync pages */
  useEffect(() => {

    setStatus("syncing");

    api.post("/api/page-config/sync")
      .then((res) => {
           console.log("FULL RESPONSE:", res);
    console.log("DATA:", res.data);
        setPages(res.data.data || []);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));

  }, []);

  /* Toggle section */

  const handleToggle = async (pageId, sectionId, currentValue) => {

    const newValue = !currentValue;
    const key = `${pageId}_${sectionId}`;

    /* optimistic update */

    setPages((prev) =>
      prev.map((p) =>
        p.pageId !== pageId
          ? p
          : {
              ...p,
              Sections: p.Sections.map((s) =>
                s.sectionId === sectionId
                  ? { ...s, isVisible: newValue }
                  : s
              ),
            }
      )
    );

    setSaving(key);

    try {

      await api.patch(`/api/page-config/${pageId}/sections/${sectionId}`, {
        isVisible: newValue,
      });

      showToast(`Section ${newValue ? "shown" : "hidden"} successfully`);

    } catch {

      showToast("Failed to update section", "error");
        // revert UI
  setPages((prev) =>
    prev.map((p) =>
      p.pageId !== pageId
        ? p
        : {
            ...p,
            Sections: p.Sections.map((s) =>
              s.sectionId === sectionId
                ? { ...s, isVisible: currentValue } // revert
                : s
            ),
          }
    )
  );

    }

    setSaving(null);
  };

 

  /* Filter search */

  const filteredPages = (pages || [])
    .map((p) => ({
      ...p,
      Sections: (p.Sections || []).filter(
        (s) =>
          s.label.toLowerCase().includes(search.toLowerCase()) ||
          s.sectionId.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((p) => p.Sections.length > 0);

  /* Loading */

  if (status === "loading" || status === "syncing") {
    return <div className="loading">Loading sections...</div>;
  }

  if (status === "error") {
    return <div className="error">Backend connection failed</div>;
  }

  /* UI */

  return (
    <div className="sectionManager">

      <div className="header">
        <h1>Section Visibility</h1>
        <p>Control which sections appear on your website</p>
      </div>

      <div className="searchBox">
        <input
          placeholder="Search pages or sections..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="pageList">
        {filteredPages.map((page) => (
          <PageCard
            key={page.pageId}
            page={page}
            onToggle={handleToggle}
            saving={saving}
          />
        ))}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

    </div>
  );
}
